import { api } from './client';
import type { WordSense, WordForms } from './vocab';

// A curator-authored cue-card deck.
export type Audience = { audience_type?: 'marathon' | 'group'; group_id?: string | null };

export type CueSet = {
  id: string;
  curator_id: string;
  marathon_id?: string | null;
  /** Кому адресован набор: всему потоку или одной группе. */
  audience_type?: 'marathon' | 'group';
  group_id?: string | null;
  lesson_id?: string | null;
  title: string;
  description?: string;
  level?: string;
  word_count: number;
};

export type CueWord = {
  id: string;
  word: string;
  translation: string;
  explanation?: string;
  example?: string;
  example_trans?: string;
  audio_url?: string;
  level?: string;
  // AI-generated dictionary data (autofilled on add) — the curator sees exactly
  // what the student will get, and can regenerate it.
  senses?: WordSense[];
  forms?: WordForms;
};

export type CueMode = 'card' | 'choose' | 'sentence' | 'audio' | 'type';

/**
 * Что именно тренируем.
 *
 * ⚠️ У КАЖДОГО СПОСОБА СВОЙ СЧЁТ. Слово считается выученным, только когда
 * закрыты все пять; поэтому способ обязан доехать до сервера — иначе ответ
 * зачтётся не туда, и слово не закроется никогда.
 */
export type SessionOpts = {
  /** Один способ вместо всех несданных. */
  mode?: CueMode;
  /** Повторение УЖЕ выученных слов, а не обычная тренировка. */
  learned?: boolean;
};

function sessionParams(o?: SessionOpts) {
  return { mode: o?.mode, scope: o?.learned ? 'learned' : undefined };
}

// One card in the adaptive session: the word + a mode chosen by the student's
// memory strength, with options for the choice modes.
export type SessionCard = {
  word_id: string;
  word: string;
  translation: string;
  explanation?: string;
  example?: string;
  example_gap?: string;
  audio_url?: string;
  level?: string;
  box: number;
  mode: CueMode;
  stage: number; // 1..N — current learning stage (card→choose→sentence→audio→type)
  stage_total: number;
  // Which meaning of the word this card drills — a polysemous word yields one
  // card ladder per sense, each graded on its own SRS timer.
  sense_index: number;
  sense_total: number; // how many senses the word has (1 for single-sense/legacy)
  pos?: string; // part of speech for this sense
  ipa?: string; // per-sense pronunciation, when it differs
  options?: string[];
  correct_index: number;
};

export type SetInput = {
  title: string;
  description?: string;
  level?: string;
  marathon_id?: string | null;
  /** Кому адресован набор: всему потоку или одной группе. */
  audience_type?: 'marathon' | 'group';
  group_id?: string | null;
  lesson_id?: string | null;
};

export const cueCardsApi = {
  // ----- curator -----
  async listSets() {
    const { data } = await api.get('/vocab/curator/sets');
    return data as { items: CueSet[]; total: number };
  },
  async createSet(input: SetInput) {
    const { data } = await api.post('/vocab/curator/sets', input);
    return data as CueSet;
  },
  async updateSet(id: string, input: SetInput) {
    await api.put(`/vocab/curator/sets/${id}`, input);
  },
  async deleteSet(id: string) {
    await api.delete(`/vocab/curator/sets/${id}`);
  },
  async listWords(setId: string) {
    const { data } = await api.get(`/vocab/curator/sets/${setId}/words`);
    return data as { items: CueWord[]; total: number };
  },
  async addWord(setId: string, word: { word: string; translation?: string; example?: string; level?: string }) {
    const { data } = await api.post(`/vocab/curator/sets/${setId}/words`, word);
    return data as CueWord;
  },
  /**
   * Добавить СПИСОК слов одной строкой.
   *
   * ⚠️ Заказчик 05.09.2026: «просто слова через запятую пишешь — и они
   * добавляются ученикам в разделе vocabulary». Сервер сам разбирает запятые,
   * переводы строк, точки с запятой и нумерацию, отбрасывает повторы и СРАЗУ
   * раскладывает слова по личным словарям тех, кому набор адресован.
   */
  async addWordsBulk(setId: string, words: string) {
    const { data } = await api.post(`/vocab/curator/sets/${setId}/words/bulk`, { words });
    return data as {
      added: { id: string; word: string }[];
      skipped: string[];
      delivered: number;
      students: number;
    };
  },
  async removeWord(setId: string, wordId: string) {
    await api.delete(`/vocab/curator/sets/${setId}/words/${wordId}`);
  },
  // Force-regenerate a word's AI dictionary entry (senses + forms). Synchronous —
  // returns the updated word so the curator sees the corrected meanings at once.
  async regenerateWord(setId: string, wordId: string) {
    const { data } = await api.post(`/vocab/curator/sets/${setId}/words/${wordId}/regenerate`);
    return data as CueWord;
  },

  // ----- student -----
  async listMySets() {
    const { data } = await api.get('/vocab/student/sets');
    return data as { items: CueSet[]; total: number };
  },
  async getSession(setId: string, limit = 15, opts?: SessionOpts) {
    const { data } = await api.get(`/vocab/student/sets/${setId}/session`, {
      params: { limit, ...sessionParams(opts) },
    });
    return data as { items: SessionCard[]; total: number };
  },
  // Adaptive session over the student's OWN saved words (personal dictionary),
  // not a curator set — powers "Учить слова" from the vocabulary page.
  async getPersonalSession(limit = 15, opts?: SessionOpts) {
    const { data } = await api.get('/vocab/student/session', {
      params: { limit, ...sessionParams(opts) },
    });
    return data as { items: SessionCard[]; total: number };
  },
};
